import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import SEOHead from '../components/SEOHead';

const StaffContent = ({ loading, error, staff, onRetry }) => {
  if (loading) {
    return <div className="text-center">Loading staff information...</div>;
  }
  if (error) {
    return (
      <div className="text-center">
        <p style={{ color: '#e74c3c', marginBottom: '1rem' }}>{error}</p>
        <button onClick={onRetry} className="btn-primary">Try Again</button>
      </div>
    );
  }
  if (staff.length === 0) {
    return <div className="text-center">No staff information available at this time.</div>;
  }
  return (
    <div className="staff-grid">
      {staff.map((member) => (
        <div key={member.id} className="staff-card">
          <div className="staff-image">
            <img 
              src={member.image} 
              alt={`${member.name} - ${member.title} at Sereno Spa`}
            />
          </div>
          <div className="staff-info">
            <h3 className="staff-name">{member.name}</h3>
            <p className="staff-bio">{member.bio}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

const About = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStaff = useCallback(async (retries = 3) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${backendUrl}/api/staff`);
      setStaff(response.data);
      setLoading(false);
      setError(null);
    } catch (err) {
      if (retries > 0) {
        setTimeout(() => fetchStaff(retries - 1), 2000);
      } else {
        setError('Failed to load staff information');
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  return (
    <div className="about-page">
      <SEOHead 
        title="About Sereno Spa | Luxury Day Spa in Livingston Manor, NY"
        description="Learn about Sereno Spa's mission to provide serenity for your skin. Meet our expert staff including Jamie Waichman, RN/Esthetician, and discover our commitment to luxury wellness in Livingston Manor."
        canonical="https://serenodayspa.com/about"
        ogTitle="About Sereno Spa - Expert Spa Team & Luxury Treatments"
        ogDescription="Meet the expert team behind Sereno Spa in Livingston Manor, NY. Professional estheticians dedicated to providing personalized spa treatments and wellness services."
      />
      <section className="section">
        <div className="container">
          <h1 className="section-title">About Sereno Spa</h1>
          
          <div className="about-content">
            <div className="about-text">
              <h2>Serenity for Your Skin</h2>
              <p>
                At Sereno Spa, we believe that true beauty comes from within, and our mission is to help you discover and nurture that inner radiance. Located in the heart of Livingston Manor, New York, our luxury spa offers a tranquil escape from the demands of daily life. We are committed to using only results driven products. Providing personalized treatments tailored to your needs while maintaining the highest standards of cleanliness and safety. Creating a welcoming, inclusive environment for all guests and continuing education to offer the latest in spa technology and techniques.
              </p>
            </div>
            
            <div className="about-image">
              <img 
                src="https://customer-assets.emergentagent.com/job_fe1c91c4-a8c1-49c7-93bd-706bc696132c/artifacts/yn6qr8bb_IMG_6344.JPG" 
                alt="Sereno Spa - Interior Reception"
              />
            </div>
          </div>

          {/* Our Staff Section */}
          <div className="staff-section">
            <h2 className="section-title">Our Staff</h2>
            <StaffContent 
              loading={loading} 
              error={error} 
              staff={staff} 
              onRetry={() => { setLoading(true); setError(null); fetchStaff(); }} 
            />
          </div>

          <div className="location-info">
            <h2 className="section-title">Visit Our Spa</h2>
            <div className="location-grid">
              <div className="location-map">
                <div className="map-container">
                  <iframe
                    src="https://www.google.com/maps?q=Sereno+Spa+43+Main+St+%231A+Livingston+Manor+NY+12758&hl=en&z=15&output=embed"
                    width="100%"
                    height="400"
                    style={{ border: 0, borderRadius: '12px' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Sereno Spa - 43 Main St #1A, Livingston Manor, NY 12758"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>

          <div className="reviews-widget" data-testid="google-reviews-widget">
            <div className="elfsight-app-40c826aa-0451-40db-beb0-505d80693a09" data-elfsight-app-lazy></div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;